import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import { SHIFT_SCOPE } from '@/components/shortcuts';
import type { ShiftFlowValue } from './useShiftFlow';
import { useShiftDialogKeys } from './useShiftDialogKeys';
import { shiftModalHeader } from './shiftMeta';
import { ShiftScope } from './ShiftScope';
import { ShiftCountStep } from './components/ShiftCountStep';
import { ShiftApproverStep } from './components/ShiftApproverStep';
import { ShiftClosedStep } from './components/ShiftClosedStep';
import { ShiftModalFooter } from './components/ShiftModalFooter';

/**
 * The shift open/close modal shell. Render-only — every value comes from
 * `useShiftFlow` (passed down from `ShiftActionButton`). The body is the
 * blind-count step, the admin co-sign step once a discrepancy advances, or the
 * closed shift's recorded figures; `ShiftModalFooter` holds the step's
 * actions. Cancel / the close button always dismiss.
 *
 * Keyboard-driven (bos-seller#60): `ShiftScope` suspends the sale screen's own
 * shortcuts while open (`F1` stays live, like every other overlay).
 * `useShiftDialogKeys` runs the step's own action (`confirmStep`, the one the
 * footer's primary button runs) on `Enter` from anywhere in the dialog; `Esc`
 * cancels via `Modal.Backdrop`'s own dismiss-on-Escape (see `PaymentModal`) —
 * except on the co-sign PIN, where `Esc` belongs to the PIN entry
 * (bos-seller#61).
 */
export const ShiftModal: React.FC<ShiftFlowValue> = (flow) => {
    const { t } = useTranslation('pages');
    const {
        mode,
        isModalOpen,
        closeModal,
        step,
        selectedApprover,
        confirmStep,
    } = flow;
    // On the co-sign PIN, `Esc` clears the entry or steps back to the picker
    // (`ShiftApproverPin`), so the dialog must not also treat it as dismiss.
    const isApproverPinShowing =
        step === 'approve' && selectedApprover !== null;
    const header = shiftModalHeader(step, mode);

    useShiftDialogKeys({ scope: SHIFT_SCOPE, onConfirm: confirmStep });

    return (
        <>
            {isModalOpen ? <ShiftScope /> : null}
            <Modal.Backdrop
                isKeyboardDismissDisabled={isApproverPinShowing}
                isOpen={isModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeModal();
                    }
                }}
                variant="blur"
            >
                <Modal.Container placement="center" size="sm">
                    <Modal.Dialog {...getNodeTestIdProp('seller-shift-modal')}>
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Icon>
                                <Icon className="size-5" icon={header.icon} />
                            </Modal.Icon>
                            <Modal.Heading>{t(header.titleKey)}</Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            {step === 'closed' ? (
                                <ShiftClosedStep {...flow} />
                            ) : step === 'approve' ? (
                                <ShiftApproverStep {...flow} />
                            ) : (
                                <ShiftCountStep {...flow} />
                            )}
                        </Modal.Body>
                        <ShiftModalFooter {...flow} />
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </>
    );
};
